import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaArrowLeft, FaSave } from 'react-icons/fa';
import axios from 'axios';
import '../styles/BookingForm.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function LivemusikBookingForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    email: '',
    telefon: '',
    eventTyp: '',
    eventDatum: '',
    startzeit: '',
    eventOrt: '',
    gaeste: '',
    setDauer: '',
    musikstil: '',
    notizen: '',
    status: 'Neu',
    manuellerPreis: ''
  });

  useEffect(() => {
    if (isEdit) {
      loadBooking();
    }
  }, [id]);

  const loadBooking = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/livemusik/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const booking = response.data.booking;
      
      setFormData({
        vorname: booking.Vorname || '',
        nachname: booking.Nachname || '',
        email: booking['E-Mail'] || '',
        telefon: booking.Telefon || '',
        eventTyp: booking['Event-Typ']?.[0] || '',
        eventDatum: booking['Event-Datum'] ? booking['Event-Datum'].split('T')[0] : '',
        startzeit: booking.Startzeit || '',
        eventOrt: booking['Event Ort'] || '',
        gaeste: booking.Gästeanzahl || '',
        setDauer: booking['Set-Dauer'] || '',
        musikstil: booking.Musikstil || '',
        notizen: booking['Event-Notizen'] || '',
        status: booking.Buchungsstatus || 'Neu',
        manuellerPreis: booking['Manueller Preis aus CRM'] || ''
      });
    } catch (error) {
      console.error('Error loading Livemusik booking:', error);
      alert('Fehler beim Laden der Livemusik-Buchung');
      navigate('/livemusik');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      if (isEdit) {
        await axios.put(`${API_URL}/livemusik/${id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/livemusik`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      navigate('/livemusik');
    } catch (error) {
      console.error('Error saving Livemusik booking:', error);
      alert('Fehler beim Speichern: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="booking-form-page">
      <div className="page-header">
        <div>
          <Link to={isEdit ? `/livemusik/${id}` : '/livemusik'} className="back-button">
            <FaArrowLeft /> Zurück
          </Link>
          <h1>🎤 {isEdit ? 'Livemusik-Buchung bearbeiten' : 'Neue Livemusik-Buchung'}</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="booking-form">
        <div className="card">
          <h2>Kundendaten</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="vorname">Vorname *</label>
              <input
                type="text"
                id="vorname"
                name="vorname"
                className="form-input"
                value={formData.vorname}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="nachname">Nachname *</label>
              <input
                type="text"
                id="nachname"
                name="nachname"
                className="form-input"
                value={formData.nachname}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">E-Mail *</label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="telefon">Telefon</label>
              <input
                type="tel"
                id="telefon"
                name="telefon"
                className="form-input"
                value={formData.telefon}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <div className="card">
          <h2>Event-Details</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="eventTyp">Event-Typ *</label>
              <select
                id="eventTyp"
                name="eventTyp"
                className="form-select"
                value={formData.eventTyp}
                onChange={handleChange}
                required
              >
                <option value="">Bitte wählen...</option>
                <option value="Hochzeit">Hochzeit</option>
                <option value="Geburtstag">Geburtstag</option>
                <option value="Firmenfeier">Firmenfeier</option>
                <option value="Jubiläum">Jubiläum</option>
                <option value="Abiball">Abiball</option>
                <option value="Clubevent">Clubevent</option>
                <option value="Festival">Festival</option>
                <option value="Privatparty">Privatparty</option>
                <option value="Sonstiges">Sonstiges</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="eventDatum">Event-Datum *</label>
              <input
                type="date"
                id="eventDatum"
                name="eventDatum"
                className="form-input"
                value={formData.eventDatum}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="startzeit">Startzeit</label>
              <input
                type="time"
                id="startzeit"
                name="startzeit"
                className="form-input"
                value={formData.startzeit}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="gaeste">Gästeanzahl</label>
              <input
                type="number"
                id="gaeste"
                name="gaeste"
                className="form-input"
                value={formData.gaeste}
                onChange={handleChange}
                min="0"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="eventOrt">Event-Ort</label>
            <input
              type="text"
              id="eventOrt"
              name="eventOrt"
              className="form-input"
              value={formData.eventOrt}
              onChange={handleChange}
              placeholder="z.B. Eventlocation XYZ, Musterstraße 123, 12345 Stadt"
            />
          </div>
        </div>

        <div className="card">
          <h2>Buchungsdetails</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="setDauer">Set-Dauer</label>
              <input
                type="text"
                id="setDauer"
                name="setDauer"
                className="form-input"
                value={formData.setDauer}
                onChange={handleChange}
                placeholder="z.B. 2 Stunden, 3x45 Min"
              />
            </div>

            <div className="form-group">
              <label htmlFor="manuellerPreis">Preis (€)</label>
              <input
                type="number"
                id="manuellerPreis"
                name="manuellerPreis"
                className="form-input"
                value={formData.manuellerPreis}
                onChange={handleChange}
                min="0"
                step="0.01"
                placeholder="z.B. 1499.00"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              className="form-select"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="Neu">Neu</option>
              <option value="In Bearbeitung">In Bearbeitung</option>
              <option value="Angebot erstellt">Angebot erstellt</option>
              <option value="Bestätigt">Bestätigt</option>
              <option value="Abgeschlossen">Abgeschlossen</option>
              <option value="Abgelehnt">Abgelehnt</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="musikstil">Musikstil / Wünsche</label>
            <textarea
              id="musikstil"
              name="musikstil"
              className="form-textarea"
              value={formData.musikstil}
              onChange={handleChange}
              rows="3"
              placeholder="z.B. Pop, Rock, Jazz, Akustisch, ..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="notizen">Event-Notizen</label>
            <textarea
              id="notizen"
              name="notizen"
              className="form-textarea"
              value={formData.notizen}
              onChange={handleChange}
              rows="4"
            />
          </div>
        </div>

        <div className="form-actions">
          <Link to={isEdit ? `/livemusik/${id}` : '/livemusik'} className="btn btn-secondary">
            Abbrechen
          </Link>
          <button type="submit" className="btn btn-primary" disabled={saving} style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
            <FaSave />
            {saving ? 'Wird gespeichert...' : (isEdit ? 'Änderungen speichern' : 'Livemusik-Buchung erstellen')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default LivemusikBookingForm;
